import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Instagram, Linkedin, Youtube } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useLanguage();

  const footerLinks = {
    company: [
      { label: 'About Us', href: '#' },
      { label: 'How It Works', href: '#how-it-works' },
      { label: 'Contact', href: 'https://t.me/propindexa', external: true }
    ],
    trading: [
      { label: 'Account Plans', href: '#plans' },
      { label: 'Trading Rules', href: '#rules' },
      { label: 'Dashboard', href: '/login' }
    ],
    resources: [
      { label: 'FAQ', href: '#faq' }
    ],
    legal: [
      { label: 'Terms & Conditions', href: '/terms-conditions', isRoute: true },
      { label: 'Privacy Policy', href: '/privacy-policy', isRoute: true },
      { label: 'Risk Disclosure', href: '/risk-disclosure', isRoute: true },
      { label: 'Cookie Policy', href: '/cookie-policy', isRoute: true }
    ]
  };

  const socialLinks = [
    { icon: Instagram, href: 'https://www.instagram.com/propindexa', label: 'Instagram' },
    { icon: Youtube, href: 'https://www.youtube.com/@PropIndexa', label: 'YouTube' },
    { icon: Linkedin, href: 'https://www.linkedin.com/in/prop-indexa-5594b13a5/', label: 'LinkedIn' }
  ];

  return (
    <footer style={{
      background: 'var(--bg-secondary)',
      borderTop: '1px solid var(--border-subtle)',
      paddingTop: '80px',
      paddingBottom: '40px'
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '0 24px'
      }}>
        {/* Main Footer Content */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '48px',
          marginBottom: '64px'
        }}>
          {/* Brand Column */}
          <div style={{
            gridColumn: 'span 1'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '16px'
            }}>
              <img src="/logo.png" alt="Prop Indexa" style={{ height: '45px' }} />
              <span style={{
                fontSize: '22px',
                fontWeight: 700,
                color: 'var(--accent-gold)',
                letterSpacing: '0.02em'
              }}>
                PROP INDEXA
              </span>
            </div>
            <p style={{
              fontSize: '15px',
              lineHeight: 1.6,
              color: 'var(--text-muted)',
              marginBottom: '24px'
            }}>
              {t('footer.description')}
            </p>

            {/* Social Links */}
            <div style={{
              display: 'flex',
              gap: '12px'
            }}>
              {socialLinks.map((social) => {
                const IconComponent = social.icon;
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    aria-label={social.label}
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      background: 'var(--bg-tertiary)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--text-muted)',
                      transition: 'all 0.2s ease',
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'var(--accent-gold)';
                      e.currentTarget.style.color = 'var(--bg-primary)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'var(--bg-tertiary)';
                      e.currentTarget.style.color = 'var(--text-muted)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <IconComponent size={18} />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h4 style={{
              fontSize: '16px',
              fontWeight: 600,
              color: 'var(--text-primary)',
              marginBottom: '20px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              Company
            </h4>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              {footerLinks.company.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  target={link.external ? '_blank' : undefined}
                  rel={link.external ? 'noopener noreferrer' : undefined}
                  style={{
                    color: 'var(--text-muted)',
                    textDecoration: 'none',
                    fontSize: '15px',
                    transition: 'color 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.color = 'var(--accent-gold)'}
                  onMouseLeave={(e) => e.target.style.color = 'var(--text-muted)'}
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>

          {/* Trading Links */}
          <div>
            <h4 style={{
              fontSize: '16px',
              fontWeight: 600,
              color: 'var(--text-primary)',
              marginBottom: '20px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              Trading
            </h4>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              {footerLinks.trading.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  style={{
                    color: 'var(--text-muted)',
                    textDecoration: 'none',
                    fontSize: '15px',
                    transition: 'color 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.color = 'var(--accent-gold)'}
                  onMouseLeave={(e) => e.target.style.color = 'var(--text-muted)'}
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>

          {/* Resources Links */}
          <div>
            <h4 style={{
              fontSize: '16px',
              fontWeight: 600,
              color: 'var(--text-primary)',
              marginBottom: '20px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              Resources
            </h4>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              {footerLinks.resources.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  style={{
                    color: 'var(--text-muted)',
                    textDecoration: 'none',
                    fontSize: '15px',
                    transition: 'color 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.color = 'var(--accent-gold)'}
                  onMouseLeave={(e) => e.target.style.color = 'var(--text-muted)'}
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Risk Disclaimer */}
        <div style={{
          background: 'rgba(239, 68, 68, 0.05)',
          border: '1px solid rgba(239, 68, 68, 0.2)',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '48px'
        }}>
          <h4 style={{
            fontSize: '14px',
            fontWeight: 600,
            color: 'var(--error)',
            marginBottom: '12px',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            Risk Disclaimer
          </h4>
          <p style={{
            fontSize: '13px',
            lineHeight: 1.6,
            color: 'var(--text-muted)'
          }}>
            Trading synthetic indices involves substantial risk of loss and is not suitable for all investors. Past performance is not indicative of future results. You should carefully consider whether trading is suitable for you in light of your circumstances, knowledge, and financial resources. All trading decisions are made at your own risk.
          </p>
        </div>

        {/* Bottom Bar */}
        <div style={{
          paddingTop: '32px',
          borderTop: '1px solid var(--border-subtle)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '24px'
        }}>
          <p style={{
            fontSize: '14px',
            color: 'var(--text-muted)'
          }}>
            © {currentYear} Prop Indexa. {t('footer.allRightsReserved')}
          </p>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '24px',
            flexWrap: 'wrap'
          }}>
            {/* Language Switcher */}
            <LanguageSwitcher variant="footer" />
            
            {footerLinks.legal.map((link) => (
              <Link
                key={link.label}
                to={link.href}
                style={{
                  color: 'var(--text-muted)',
                  textDecoration: 'none',
                  fontSize: '14px',
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.color = 'var(--text-primary)'}
                onMouseLeave={(e) => e.target.style.color = 'var(--text-muted)'}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;